import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { PLAN_DIM_MODAL_RADIUS } from 'pages/PlanRevamp/PlanPageUIRevamp/planDimSelectionModalStyles';

import colors from './dataTheme';

interface Properties {
  isOpen: boolean;
  initialName: string;
  title?: string;
  fieldLabel?: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void> | void;
}

const EditSourceModal = ({
  isOpen,
  initialName,
  title = 'Edit source',
  fieldLabel = 'Source name',
  onClose,
  onSave,
}: Properties) => {
  const [name, setName] = useState(initialName);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setName(initialName);
  }, [isOpen, initialName]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== initialName && !isSaving;

  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg='blackAlpha.400' />
      <ModalContent maxW='440px' borderRadius={PLAN_DIM_MODAL_RADIUS} overflow='hidden' mx={4}>
        <ModalHeader
          px={6}
          py={4}
          borderBottom='1px solid'
          borderColor={colors.border}
          bg={colors.surface}
        >
          <Text {...PLAN_TEXT.modalTitle} color={colors.textDark}>
            {title}
          </Text>
        </ModalHeader>

        <ModalBody px={6} py={5} bg={colors.surface}>
          <Text {...PLAN_TEXT.labelSmMedium} color={colors.textSecondary} mb={2}>
            {fieldLabel}
          </Text>
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') save();
            }}
            placeholder={`Enter ${fieldLabel.toLowerCase()}`}
            h='44px'
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            borderColor={colors.border}
            {...PLAN_TEXT.body}
            color={colors.textDark}
            _placeholder={{ color: colors.textSecondary }}
            _focusVisible={{ borderColor: colors.primary, boxShadow: 'none' }}
          />
        </ModalBody>

        <ModalFooter
          px={6}
          py={4}
          borderTop='1px solid'
          borderColor={colors.border}
          bg={colors.surface}
        >
          <Flex w='100%' gap={3}>
            <Button
              flex='1'
              h='44px'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              variant='outline'
              borderColor={colors.border}
              bg={colors.surface}
              {...PLAN_TEXT.button}
              fontSize='14px'
              color={colors.textDark}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              flex='1'
              h='44px'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={colors.primary}
              color={colors.white}
              {...PLAN_TEXT.button}
              fontSize='14px'
              isDisabled={!canSave}
              isLoading={isSaving}
              _hover={{ bg: colors.primaryHover }}
              _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
              onClick={save}
            >
              Save changes
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditSourceModal;
